/**
 * A right-click menu: shown where the pointer was (or beside the element a
 * keyboard opened it for), kept inside the window, closed by Escape, a click
 * elsewhere, scrolling or the window losing focus. Arrow keys move between
 * items; an item with `items` opens a list of its own beside it.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface MenuItem {
  readonly label: string;
  readonly onSelect?: () => void;
  /** A list beside this item instead of an action ("Move to"). */
  readonly items?: readonly MenuItem[];
  readonly danger?: boolean;
  readonly disabled?: boolean;
  /** A line above this item, to group related actions. */
  readonly separator?: boolean;
}

export interface MenuAt {
  readonly x: number;
  readonly y: number;
  readonly label: string;
  readonly items: readonly MenuItem[];
}

/** Where a menu opened from the keyboard goes: just inside the element. */
export function menuAtElement(
  element: Element | null,
  label: string,
  items: readonly MenuItem[],
): MenuAt {
  const r = element?.getBoundingClientRect();
  return { x: (r?.left ?? 0) + 24, y: (r?.top ?? 0) + (r ? r.height / 2 : 0), label, items };
}

function List({
  items,
  label,
  x,
  y,
  onClose,
  autoFocus,
}: {
  items: readonly MenuItem[];
  label: string;
  x: number;
  y: number;
  onClose: () => void;
  autoFocus: boolean;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [sub, setSub] = useState<number | null>(null);

  // Inside the window: flip left or up when there is no room.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const { width, height } = el.getBoundingClientRect();
    const left = x + width > window.innerWidth - 8 ? Math.max(8, x - width) : x;
    const top =
      y + height > window.innerHeight - 8 ? Math.max(8, window.innerHeight - height - 8) : y;
    setPos({ left, top });
  }, [x, y]);

  useEffect(() => {
    if (autoFocus) {
      ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
    }
  }, [autoFocus]);

  const buttons = () =>
    Array.from(
      ref.current?.querySelectorAll<HTMLButtonElement>(':scope > [role="menuitem"]') ?? [],
    );
  const step = (d: number) => {
    const all = buttons().filter((b) => !b.disabled);
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    all[(at + d + all.length) % all.length]?.focus();
  };

  return (
    <div
      ref={ref}
      className="context-menu"
      role="menu"
      aria-label={label}
      style={{ left: pos.left, top: pos.top }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          step(1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          step(-1);
        } else if (e.key === 'ArrowRight') {
          const i = buttons().indexOf(document.activeElement as HTMLButtonElement);
          if (i >= 0 && items[i]?.items) {
            e.preventDefault();
            setSub(i);
          }
        } else if (e.key === 'ArrowLeft' && sub !== null) {
          e.preventDefault();
          e.stopPropagation();
          const i = sub;
          setSub(null);
          buttons()[i]?.focus();
        }
      }}
    >
      {items.map((item, i) => (
        <MenuEntry
          key={`${i}-${item.label}`}
          item={item}
          open={sub === i}
          onOpenSub={() => setSub(i)}
          onCloseSub={() => setSub((s) => (s === i ? null : s))}
          onClose={onClose}
          parent={ref}
        />
      ))}
    </div>
  );
}

function MenuEntry({
  item,
  open,
  onOpenSub,
  onCloseSub,
  onClose,
  parent,
}: {
  item: MenuItem;
  open: boolean;
  onOpenSub: () => void;
  onCloseSub: () => void;
  onClose: () => void;
  parent: React.RefObject<HTMLDivElement>;
}): JSX.Element {
  const ref = useRef<HTMLButtonElement>(null);
  const r = open ? ref.current?.getBoundingClientRect() : undefined;
  return (
    <>
      {item.separator && <hr className="context-sep" />}
      <button
        ref={ref}
        type="button"
        role="menuitem"
        className={item.danger ? 'danger' : undefined}
        disabled={item.disabled}
        aria-haspopup={item.items ? 'menu' : undefined}
        aria-expanded={item.items ? open : undefined}
        onMouseEnter={() => (item.items ? onOpenSub() : onCloseSub())}
        onClick={() => {
          if (item.items) {
            onOpenSub();
            return;
          }
          onClose();
          item.onSelect?.();
        }}
      >
        <span>{item.label}</span>
        {item.items && <span aria-hidden="true">›</span>}
      </button>
      {open && item.items && r && parent.current && (
        <List
          items={item.items}
          label={item.label}
          x={r.right - 4}
          y={r.top - 6}
          onClose={onClose}
          autoFocus
        />
      )}
    </>
  );
}

/** The menu, or nothing. `at` null closes it. */
export function ContextMenu({
  at,
  onClose,
}: {
  at: MenuAt | null;
  onClose: () => void;
}): JSX.Element | null {
  const root = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    if (!at) {
      return;
    }
    opener.current = document.activeElement;
    const outside = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) {
        onClose();
      }
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    const away = () => onClose();
    // The page scrolling moves what the menu belongs to; a long list inside
    // the menu scrolling does not.
    const scrolled = (e: Event) => {
      if (!(e.target instanceof Node && root.current?.contains(e.target))) {
        onClose();
      }
    };
    // Capture: closing comes before whatever else the click or key does.
    document.addEventListener('mousedown', outside, true);
    document.addEventListener('keydown', key, true);
    window.addEventListener('blur', away);
    window.addEventListener('resize', away);
    document.addEventListener('scroll', scrolled, true);
    return () => {
      document.removeEventListener('mousedown', outside, true);
      document.removeEventListener('keydown', key, true);
      window.removeEventListener('blur', away);
      window.removeEventListener('resize', away);
      document.removeEventListener('scroll', scrolled, true);
      // Back to where the person was, unless the action moved them on.
      if (opener.current instanceof HTMLElement && document.activeElement === document.body) {
        opener.current.focus();
      }
    };
  }, [at, onClose]);

  if (!at) {
    return null;
  }
  return (
    <div ref={root} className="context-root">
      <List items={at.items} label={at.label} x={at.x} y={at.y} onClose={onClose} autoFocus />
    </div>
  );
}
